/*
 * Inbox v2's page state and handlers (`page.tsx` draws them): what is open, focused and selected,
 * and what each action and key does to them. Which list is chosen is the address's
 * (`shell-nav.ts`), so the shell's rail and this page read one value. The data and the writes come
 * from `use-inbox.ts`; this owns only what the rep is looking at.
 */
import { useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import {
  INBOX_PAGE_SIZE,
  type InboxConversation,
  type InboxCounts,
  type InboxPage,
  type InboxThread,
} from "../../src/inbox-v2/contract.ts";
import { usePageShortcuts } from "../page-shortcuts.tsx";
import { type MailChoice, mailHref } from "../shell-nav.ts";
import { type Assignment, UNASSIGNED, useAssignControls } from "./assignment.tsx";
import { useInboxActions } from "./inbox-actions.ts";
import { inboxShortcuts } from "./inbox-keys.ts";
import { LABEL_VIEWS } from "./labels.ts";
import { useSnoozeActions } from "./snooze-actions.ts";
import { useTrashActions } from "./trash-actions.ts";
import {
  boxOf,
  NO_FILTERS,
  nextPlace,
  type SavedView,
  type Toast,
  useInboxAccess,
  useInboxData,
  useInboxViews,
  useKeys,
  useMedia,
  useNeedsReplyShortlist,
  useToast,
} from "./use-inbox.ts";
import { type ListControls, useChoiceReset, useListControls } from "./use-list-controls.ts";
import { useSettleSearch } from "./use-search.ts";

const BOX_NAMES = {
  inbox: "Inbox",
  snoozed: "Snoozed",
  sent: "Sent",
  done: "Done",
  trash: "Trash",
} as const;

/** Whose threads, in the scope's words: Mine, Unassigned or a colleague's name. */
function ownerName(owner: string, assignment: Assignment | null): string {
  if (owner === "me") return "Mine";
  if (owner === UNASSIGNED) return "Unassigned";
  return assignment?.people.find((person) => person.userId === owner)?.name ?? "Assigned";
}

/** The list's name: its place, and whose threads where the rail's scope narrows it. */
function viewName(
  choice: MailChoice,
  owner: string | null,
  views: readonly SavedView[],
  assignment: Assignment | null,
): string {
  const place = placeName(choice, views);
  return owner ? `${place} · ${ownerName(owner, assignment)}` : place;
}

function placeName(choice: MailChoice, views: readonly SavedView[]): string {
  switch (choice.kind) {
    case "box":
      return BOX_NAMES[choice.box];
    case "label":
      return LABEL_VIEWS.find((view) => view.view === choice.label)?.name ?? BOX_NAMES.inbox;
    case "meeting":
      return "Meeting requests";
    case "needs_reply":
      return "Needs reply";
    case "follow_up":
      return "Follow-up due";
    case "view":
      return views.find((view) => view.id === choice.id)?.name ?? BOX_NAMES.inbox;
  }
}

/**
 * The open conversation lives in the URL (`?c=`), so a reload or a shared link reopens it. A link of
 * the reply agent's old inbox (`?plan=`) opens the same conversation and is rewritten to `?c=` in
 * place, its hash kept.
 *
 * The address is the router's, read and written through it. A place chosen in the rail reaches the
 * window's address a microtask after the router has moved, and the list closes its conversation
 * before that: an address built from the window's put the old place back under the new one's URL.
 */
function useOpenInUrl(): [string | null, (id: string | null) => void] {
  const router = useRouter();
  const [openId, setOpenId] = useState<string | null>(() => {
    const search = new URLSearchParams(location.search);
    return search.get("c") ?? search.get("plan");
  });
  useEffect(() => {
    const here = router.history.location;
    const url = new URL(here.href, location.origin);
    url.searchParams.delete("plan");
    if (openId) url.searchParams.set("c", openId);
    else url.searchParams.delete("c");
    const href = `${url.pathname}${url.search}${url.hash}`;
    if (href !== here.href) void router.navigate({ href, replace: true, resetScroll: false });
  }, [openId, router]);
  return [openId, setOpenId];
}

/**
 * Marking seen on opening. A click marks the row it opened; a conversation opened by its address (a
 * link's `?c=`, an old `?plan=`, a reload, a held reply's toast) is marked once its thread is in,
 * as the click would have (step 10, round 1). Once per opening: a rep who then marks it unseen keeps
 * it unseen.
 */
function useSeenOnOpen(
  openId: string | null,
  thread: InboxThread | undefined,
  markSeen: (row: InboxConversation) => Promise<boolean>,
  onToast: (toast: Toast) => void,
) {
  const marked = useRef<string | null>(null);
  const opened = thread?.conversation;
  /*
   * Marked once per opening; a failure says so and offers Try again, which marks it once more.
   * Nothing tries again by itself, so a server refusing it cannot set a loop going (W5, F07).
   */
  const mark = (row: InboxConversation) => {
    marked.current = row.id;
    void markSeen(row).then((committed) => {
      if (committed) return;
      onToast({
        text: "Could not mark this conversation seen.",
        undo: null,
        action: { label: "Try again", run: () => mark(row) },
      });
    });
  };
  useEffect(() => {
    if (openId === null) {
      marked.current = null;
      return;
    }
    if (marked.current === openId || opened?.id !== openId) return;
    mark(opened);
  });
  return mark;
}

/** The row `step` away on the list, held at its ends; none on an empty list. */
function stepRow(visible: readonly InboxConversation[], from: string | null, step: 1 | -1) {
  const index = visible.findIndex((row) => row.id === from);
  return visible[Math.min(visible.length - 1, Math.max(0, index + step))];
}

/**
 * A row moved to, brought into view; and given the focus where the list holds it, so the list stays
 * one Tab stop that J, K and the arrows move within (step 10, round 2).
 */
function showRow(id: string) {
  const row = document.querySelector<HTMLElement>(
    `.lcr-list [data-row-id="${CSS.escape(id)}"]:not([data-pinned])`,
  );
  row?.scrollIntoView({ block: "nearest" });
  if (document.activeElement?.closest(".lcr-list .lcr-row")) row?.focus({ preventScroll: true });
}

function useSelection(visible: readonly InboxConversation[]) {
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [selecting, setSelecting] = useState(false);
  return {
    selected,
    selecting,
    allShown: visible.length > 0 && visible.every((row) => selected.has(row.id)),
    clear: () => setSelected(new Set()),
    shown: () => setSelected(new Set(visible.map((row) => row.id))),
    toggle: (id: string) => {
      const next = new Set(selected);
      if (!next.delete(id)) next.add(id);
      setSelected(next);
    },
    /** A phone's Select, or a held row: the checkboxes show, ticked or not. */
    startSelecting: (id?: string) => {
      setSelecting(true);
      if (id !== undefined) setSelected(new Set([...selected, id]));
    },
    stop: () => {
      setSelected(new Set());
      setSelecting(false);
    },
  };
}

/*
 * What an action applies to: the selection, else the open conversation, else the focused row.
 * Rows kept from the list shown before answer an older question while the next one loads, so no
 * action takes them until it settles (W4); the open conversation is read on its own and stays.
 */
function actionTargets(
  selected: ReadonlySet<string>,
  visible: readonly InboxConversation[],
  thread: InboxThread | undefined,
  focusedId: string | null,
  kept: boolean,
): InboxConversation[] {
  if (selected.size) return kept ? [] : visible.filter((row) => selected.has(row.id));
  const one =
    thread?.conversation ?? (kept ? undefined : visible.find((row) => row.id === focusedId));
  return one ? [one] : [];
}

/** What the shell hands the page: the phone breakpoint, the chosen list, and a way to change it. */
export type InboxPageShell = {
  readonly phone: boolean;
  /** Whose list choices to keep: the order per view and the mode are this person's own. */
  readonly userId: string;
  readonly choice: MailChoice;
  /** Whose threads the address narrows to (the rail's scope); null is everyone. */
  readonly owner: string | null;
  /** Follows a link in place, as the rail's rows do. */
  readonly go: (href: string) => void;
  /**
   * Runs a move to another conversation now, or once the rep agrees to lose text the reply box
   * could not keep (the shell's unsaved-work guard). Kept text never asks: it is there on return.
   */
  readonly guarded: (move: () => void) => void;
};

/** The keys panel's list (`inbox-keys.ts`): none on touch. */
const shortcutsFor = (showKeys: boolean, agent: boolean, assignment: boolean) =>
  showKeys ? inboxShortcuts(agent, assignment) : null;

/**
 * Where Snooze's choices open: under the bulk bar, on a row, under the list's heading, or under the
 * open conversation's own Snooze.
 */
export type SnoozeAnchor = "bar" | "head" | "thread" | { row: string };
type Snoozing = { anchor: SnoozeAnchor; rows: InboxConversation[] } | null;

/**
 * Where H opens Snooze for these rows: the bar for a selection, the head of the open conversation
 * when that is the one, else the row when it is shown.
 */
function anchorFor(
  rows: readonly InboxConversation[],
  selected: number,
  visible: readonly InboxConversation[],
  openId: string | null,
): SnoozeAnchor {
  if (selected > 0) return "bar";
  const [one] = rows;
  if (one && one.id === openId) return "thread";
  return one && visible.some((row) => row.id === one.id) ? { row: one.id } : "head";
}

/**
 * What the list asks for (step 9): the view's own filters, with the agent state only while the
 * person's switch is on, and the first page while a state the switch no longer allows waits to be
 * dropped, so no request goes out at the agent list's page.
 */
function listView(list: ListControls, allowed: boolean) {
  const agent = allowed ? list.shown.agent : null;
  const dropping = list.shown.agent !== null && agent === null;
  return {
    agentStatus: agent,
    filters: { ...list.shown, agent },
    offset: dropping ? 0 : list.offset,
  };
}

/**
 * The switch going off drops the agent state from the filters, as a filter change does: the page
 * starts again at its first page and the selection clears. It is dropped, not held: turned on again,
 * the list starts without it.
 */
function useDropAgentState(list: ListControls, allowed: boolean, clear: () => void): void {
  const held = list.filters.agent !== null;
  useEffect(() => {
    if (allowed || !held) return;
    list.setFilters({ ...list.filters, agent: null });
    clear();
  });
}

/**
 * A page past the last row, once rows have left it (the last one handled on the last page), steps
 * back to the last page that has rows, rather than saying "51–50 of 50" over an empty list.
 */
function useClampedOffset(list: ListControls, page: InboxPage | undefined): void {
  const past = page !== undefined && page.total > 0 && list.offset >= page.total;
  const last = page ? Math.floor((page.total - 1) / INBOX_PAGE_SIZE) * INBOX_PAGE_SIZE : 0;
  useEffect(() => {
    if (past) list.setOffset(last);
  });
}

/**
 * The replies the reading pane offers while nothing is open (`nothing-open.tsx`), and the one Enter
 * opens: with nothing open and no list row holding the focus, the first of them, as its Open says.
 * A phone draws no reading pane while nothing is open, so it asks for nothing. They come from
 * Meeting requests while any wait, else from Needs reply, once the counts have said (LEA-494).
 */
function useNextReplies(
  workspaceId: string,
  phone: boolean,
  openId: string | null,
  owner: string | null,
  counts: { readonly data?: InboxCounts | undefined; readonly isError: boolean },
) {
  const place = nextPlace(counts.data, counts.isError);
  const shortlist = useNeedsReplyShortlist(workspaceId, !phone && openId === null, owner, place);
  const next = () => {
    if (openId !== null || document.activeElement?.closest(".lcr-row")) return undefined;
    return shortlist.data?.[0];
  };
  return {
    next,
    /** For the page: the shortlist, and where it reads from (null while the counts have not said). */
    page: { shortlist, nextPlace: place },
  };
}

/**
 * The open conversation's two head menus a key opens: the label menu (L) and More (".", Snooze,
 * Mark seen, Delete). `closeMenus` puts both away, as opening another conversation or Escape
 * does.
 */
function useHeadMenus() {
  const [relabelOpen, setRelabelOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  return {
    relabelOpen,
    setRelabelOpen,
    moreOpen,
    setMoreOpen,
    anyMenuOpen: relabelOpen || moreOpen,
    closeMenus: () => {
      setRelabelOpen(false);
      setMoreOpen(false);
    },
  };
}

/** After a write that takes rows out of this list: the open one closes, the ticks clear. */
function closeAfter(openId: string | null, setOpenId: (id: null) => void, clear: () => void) {
  return (rows: readonly InboxConversation[], gone: boolean) => {
    if (gone && rows.some((row) => row.id === openId)) setOpenId(null);
    clear();
  };
}

/** Another place keeps the scope (`choose`); Everyone keeps the place (`everyone`). */
function scopeMoves(shell: InboxPageShell, workspaceId: string, choice: MailChoice) {
  return {
    choose: (next: MailChoice) => shell.go(mailHref(workspaceId, next, shell.owner)),
    everyone: () => shell.go(mailHref(workspaceId, choice)),
  };
}

export function useInboxPage(workspaceId: string, shell: InboxPageShell) {
  const { phone, choice, owner } = shell;
  const finePointer = useMedia("(hover: hover) and (pointer: fine)");
  const views = useInboxViews(workspaceId);
  const list = useListControls(workspaceId, shell.userId, choice, views.data);
  const [openId, setOpenId] = useOpenInUrl();
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const menus = useHeadMenus();
  const [bulkRelabel, setBulkRelabel] = useState(false);
  const [snoozing, setSnoozing] = useState<Snoozing>(null);
  const [replyFocus, setReplyFocus] = useState(0);
  const [toast, setToast] = useToast();
  const searchField = useRef<HTMLInputElement>(null);
  const showKeys = !phone && finePointer;
  const agent = useInboxAccess(workspaceId).data?.replyAgent === true;
  const { agentStatus, filters, offset } = listView(list, agent);
  const data = useInboxData(
    workspaceId,
    { choice, owner, q: list.q, filters, offset, sort: list.sort },
    openId,
    list.mode === "highlights",
    list.ready,
  );
  useSettleSearch(list.search, data.list.page, data.list.placeholder, data.list.refused !== null);
  useClampedOffset(list, data.list.page);
  const actions = useInboxActions(workspaceId, setToast);
  const markOpened = useSeenOnOpen(openId, data.thread.data, actions.markSeen, setToast);
  const sleep = useSnoozeActions(workspaceId, setToast);
  const trashing = useTrashActions(workspaceId, setToast);
  const { visible } = data;
  const nextReplies = useNextReplies(workspaceId, phone, openId, owner, data.counts);
  const selection = useSelection(visible);
  useDropAgentState(list, agent, selection.clear);
  useChoiceReset(choice, () => {
    selection.stop();
    setOpenId(null);
    setFocusedId(null);
    setSnoozing(null);
  });

  const open = (row: InboxConversation) =>
    shell.guarded(() => {
      setOpenId(row.id);
      setFocusedId(row.id);
      menus.closeMenus();
      assigning.setOpen(false);
      markOpened(row);
    });
  const targets = () =>
    actionTargets(selection.selected, visible, data.thread.data, focusedId, data.list.placeholder);
  const assigning = useAssignControls(workspaceId, data.counts.data, actions, targets, selection);
  usePageShortcuts(shortcutsFor(showKeys, agent, assigning.assignment !== null));
  const after = closeAfter(openId, setOpenId, selection.clear);
  const box = boxOf(choice, data.views);
  const handlers = {
    done: async (rows = targets()) => after(rows, await actions.done(rows)),
    // Delete into Trash, or Restore rows listed there: either way they leave the list shown.
    trash: async (rows = targets()) => after(rows, await trashing.trash(rows, box === "trash")),
    relabel: async (label: InboxLabelCode | null, rows = targets()) => {
      menus.setRelabelOpen(false);
      setBulkRelabel(false);
      await actions.relabel(label, rows);
      selection.clear();
    },
    meeting: (row: InboxConversation, value: boolean) => void actions.meeting(row, value),
    confirm: async (rows = targets()) => {
      await actions.confirm(rows);
      selection.clear();
    },
    seen: (rows = targets(), value?: boolean) => void actions.seen(rows, value),
    /** Opens Snooze's choices for these rows, where the rep's eyes are. */
    snoozeMenu: (rows = targets(), anchor?: SnoozeAnchor) => {
      if (!rows.length) return;
      const reading = data.thread.data?.conversation.id ?? null;
      setSnoozing({
        anchor: anchor ?? anchorFor(rows, selection.selected.size, visible, reading),
        rows,
      });
    },
    snooze: async (until: string) => {
      const rows = snoozing?.rows ?? [];
      setSnoozing(null);
      await sleep.snooze(rows, until);
      after(rows, box !== "snoozed");
    },
    unsnooze: async (rows = snoozing?.rows ?? []) => {
      setSnoozing(null);
      await sleep.unsnooze(rows);
      after(rows, box === "snoozed");
    },
    /** The bulk bar's Unsnooze in the Snoozed box: the ticked ones, now. */
    wake: async () => {
      const rows = targets();
      setSnoozing(null);
      await sleep.unsnooze(rows);
      after(rows, box === "snoozed");
    },
  };
  const move = (step: 1 | -1) => {
    const next = stepRow(visible, focusedId ?? openId, step);
    if (!next) return;
    setFocusedId(next.id);
    if (!phone) open(next);
    showRow(next.id);
  };
  /** Esc closes the innermost thing: a menu, then the selection, then a phone's thread. */
  const onEscape = () => {
    if (menus.anyMenuOpen || bulkRelabel || assigning.anyOpen || snoozing) {
      menus.closeMenus();
      setBulkRelabel(false);
      assigning.close();
      setSnoozing(null);
      return;
    }
    if (selection.selected.size || selection.selecting) return selection.stop();
    if (phone) shell.guarded(() => setOpenId(null));
  };
  useKeys(
    {
      j: () => move(1),
      k: () => move(-1),
      x: () => (focusedId ? selection.toggle(focusedId) : undefined),
      e: () => void handlers.done(),
      "#": () => void handlers.trash(),
      h: () => handlers.snoozeMenu(),
      // With rows ticked, U is the bar's "Mark unseen" and Shift+I its "Mark seen".
      u: () => (selection.selected.size ? handlers.seen(targets(), false) : handlers.seen()),
      I: () => handlers.seen(targets(), true),
      c: () => void handlers.confirm(),
      l: () => (selection.selected.size ? setBulkRelabel(true) : menus.setRelabelOpen(true)),
      m: () => assigning.openFor(selection.selected.size > 0),
      ".": () => (openId && !selection.selected.size ? menus.setMoreOpen(true) : undefined),
      r: () => setReplyFocus((n) => n + 1),
      // A phone has no `/` to press; its field is always in view.
      "/": () => (phone ? undefined : searchField.current?.focus()),
      Enter: () => {
        const row = visible.find((candidate) => candidate.id === focusedId) ?? nextReplies.next();
        if (row) open(row);
      },
    },
    onEscape,
  );

  return {
    workspaceId,
    phone,
    showKeys,
    choice,
    box,
    owner,
    name: viewName(choice, owner, data.views, assigning.assignment),
    list,
    /** The person's reply agent switch (`/access`), which offers the Status filter's agent group. */
    replyAgent: agent,
    /** The reply agent status narrowing the list, newest first and never saved; null when none. */
    agentStatus,
    setFilters: (next: Parameters<typeof list.setFilters>[0]) => {
      list.setFilters(next);
      selection.clear();
    },
    /** Takes the filters and the operators off and keeps the words. */
    removeFilters: () => {
      list.search.setTokens([]);
      list.setFilters(NO_FILTERS);
    },
    setOffset: (next: number) => {
      list.setOffset(next);
      selection.clear();
      document.querySelector(".lcr-i2 .lcr-list")?.scrollTo({ top: 0 });
    },
    openId,
    ...nextReplies.page,
    move,
    close: () => shell.guarded(() => setOpenId(null)),
    /** A held reply's toast opens its conversation, wherever the list has moved meanwhile. */
    reopen: (id: string) =>
      shell.guarded(() => {
        setOpenId(id);
        setFocusedId(id);
      }),
    focusedId,
    selection,
    /** Rows are being ticked, or a phone chose Select: the bar and every checkbox show. */
    selecting: selection.selected.size > 0 || selection.selecting,
    ...menus,
    bulkRelabel,
    setBulkRelabel,
    assigning,
    snoozing,
    closeSnooze: () => setSnoozing(null),
    replyFocus,
    toast,
    setToast,
    searchField,
    data,
    open,
    ...handlers,
    ...scopeMoves(shell, workspaceId, choice),
  };
}

export type InboxPageModel = ReturnType<typeof useInboxPage>;
